/**
 * R1 W2 — staged suggestions and applied AI edits.
 *
 * Since R1 W1 the backend has two kinds of AI change to written text:
 *  - a requested EDIT (`draft_edit_applied`): saved at once, undoable;
 *  - a STAGED suggestion (`draft_patch_proposed` with `mode: 'staged'`): the
 *    section is untouched until the user accepts it.
 * Legacy proposals (`mode` absent / `applied`) already wrote their text into the
 * section and keep R0's strip unchanged.
 *
 * Pure helpers shared by the agent store, the editor and the panel live here,
 * so each rule has one implementation.
 */

import {
  ApiRequestError,
  resolveStagedSuggestion,
  type CitationRef,
  type PendingDraftProposal,
  type ResolvedSegmentSnapshot,
} from '@/lib/api'
import { applyHunkDecisions, buildSegmentDiff, type HunkDecision } from '@/components/drafting/segmentDiff'

// ===================
// Types
// ===================

/** Fields a review-strip preview carries for R1 staged suggestions. */
export interface StagedSuggestionFields {
  /** Absent = legacy (`applied`): the AI text is already in the section. */
  mode?: 'applied' | 'staged'
  /** Section version the suggestion was made against. */
  baseSegmentVersion?: number | null
  /** The suggestion's own [N] refs; null/absent = the section's refs. */
  afterCitationRefs?: CitationRef[] | null
  /** The suggestion's own [LN] refs; null/absent = the section's refs. */
  afterLibraryCitationRefs?: CitationRef[] | null
  /** Accept was refused because the section moved on (409 SUGGESTION_OUTDATED). */
  outdated?: boolean
}

export interface PreviewLike extends StagedSuggestionFields {
  proposalId: string
  sectionId: string
  before: string
  after: string
}

/** One staged suggestion still waiting on the document (per-document list). */
export interface PendingSuggestion {
  proposalId: string
  sectionId: string
  heading: string | null
  before: string
  after: string
  baseSegmentVersion: number | null
  afterCitationRefs: CitationRef[] | null
  afterLibraryCitationRefs: CitationRef[] | null
  turnId: string | null
}

interface SegmentLike {
  sectionId: string
  content: string
}

// ===================
// Mode
// ===================

export function isStagedPreview(preview: StagedSuggestionFields | null | undefined): boolean {
  return preview?.mode === 'staged'
}

/**
 * The text the strip diffs against. A legacy proposal's `before` is what the
 * AI replaced (its text is already in the section). A staged suggestion is
 * shown over the section's CURRENT text — the editor never holds the AI text —
 * so a hand edit made since the suggestion is what the user sees as "before".
 */
export function previewDiffBase(
  preview: PreviewLike,
  segments: ReadonlyArray<SegmentLike>,
): string {
  if (!isStagedPreview(preview)) return preview.before
  const current = segments.find((segment) => segment.sectionId === preview.sectionId)
  return current ? current.content : preview.before
}

/**
 * The HTML to send as `mergedHtml` for a staged accept, or undefined for a
 * whole accept. Built over the same base the editor renders, so what the user
 * ticked is what gets saved. Pending hunks count as accepted ("Accept" takes
 * everything not rejected), matching the legacy partial accept.
 */
export function stagedMergedHtml(
  preview: PreviewLike & { hunkDecisions?: Record<string, HunkDecision> },
  segments: ReadonlyArray<SegmentLike>,
): string | undefined {
  const decisions = preview.hunkDecisions ?? {}
  if (!Object.values(decisions).some((decision) => decision === 'rejected')) return undefined
  const diff = buildSegmentDiff(previewDiffBase(preview, segments), preview.after)
  return applyHunkDecisions(diff.segments, decisions, 'accepted')
}

// ===================
// Citation refs
// ===================

/**
 * While a staged suggestion is on screen its [N]/[LN] markers belong to the
 * suggestion, not the section, so they resolve against its own refs. Returns
 * the refs map with that one section swapped; the input map is not mutated.
 * Legacy proposals and suggestions without their own refs leave it untouched.
 */
export function withPreviewCitationRefs(
  bySectionId: Map<string, CitationRef[]>,
  preview: PreviewLike | null | undefined,
  kind: 'citation' | 'library',
): Map<string, CitationRef[]> {
  const refs = previewOwnRefs(preview, kind)
  if (!preview || refs === null) return bySectionId
  const next = new Map(bySectionId)
  next.set(preview.sectionId, refs)
  return next
}

/** The suggestion's own refs when it is a live staged preview that carries them; else null. */
export function previewOwnRefs(
  preview: PreviewLike | null | undefined,
  kind: 'citation' | 'library',
): CitationRef[] | null {
  if (!preview || !isStagedPreview(preview) || preview.outdated) return null
  const refs = kind === 'citation' ? preview.afterCitationRefs : preview.afterLibraryCitationRefs
  return Array.isArray(refs) ? refs : null
}

// ===================
// Parsing
// ===================

function readRefs(value: unknown): CitationRef[] | null {
  return Array.isArray(value) ? (value as CitationRef[]) : null
}

/** A session-load pending proposal as a list entry, or null when it is not staged. */
export function pendingSuggestionFromProposal(
  pending: PendingDraftProposal | null | undefined,
): PendingSuggestion | null {
  if (!pending || pending.mode !== 'staged') return null
  return {
    proposalId: pending.proposalId,
    sectionId: pending.sectionId,
    heading: typeof pending.heading === 'string' && pending.heading.trim() ? pending.heading : null,
    before: pending.before,
    after: pending.after,
    baseSegmentVersion: typeof pending.baseSegmentVersion === 'number' ? pending.baseSegmentVersion : null,
    afterCitationRefs: readRefs(pending.afterCitationRefs),
    afterLibraryCitationRefs: readRefs(pending.afterLibraryCitationRefs),
    turnId: pending.turnId,
  }
}

/**
 * Staged fields of a live `draft_patch_proposed` payload. The event's
 * `segments` carry the suggested text WITH the suggestion's refs; they are
 * never written into the store's segments (the section is untouched).
 */
export function stagedFieldsFromEvent(
  data: Record<string, unknown>,
  sectionId: string,
): StagedSuggestionFields {
  if (data['mode'] !== 'staged') return {}
  const segments = Array.isArray(data['segments']) ? (data['segments'] as unknown[]) : []
  const own = segments.find((segment): segment is Record<string, unknown> => (
    typeof segment === 'object' && segment !== null
    && (segment as Record<string, unknown>)['sectionId'] === sectionId
  )) ?? (segments.length === 1 && typeof segments[0] === 'object' && segments[0] !== null
    ? segments[0] as Record<string, unknown>
    : undefined)
  const base = data['baseSegmentVersion']
  return {
    mode: 'staged',
    baseSegmentVersion: typeof base === 'number' ? base : null,
    afterCitationRefs: own ? readRefs(own['citationRefs']) : null,
    afterLibraryCitationRefs: own ? readRefs(own['libraryCitationRefs']) : null,
  }
}

// ===================
// Deciding a staged suggestion
// ===================

export type StagedDecisionOutcome =
  /** Applied. `segment` is the section as saved (null on a repeat from another tab). */
  | { kind: 'accepted'; segment: ResolvedSegmentSnapshot | null }
  /** Rejected; nothing was written. */
  | { kind: 'rejected' }
  /** Accept refused: the section moved on. Nothing was applied. */
  | { kind: 'outdated'; currentVersion: number | null; currentContent: string | null }
  /** The section (or suggestion) no longer exists. */
  | { kind: 'gone' }
  /** Reject refused: someone already accepted it. */
  | { kind: 'already_accepted' }
  /**
   * Anything else — the strip stays so the user can try again. `message` is the
   * server's own wording when it refused for a reason the user can act on (403:
   * frozen document, section assigned to someone else); null otherwise.
   */
  | { kind: 'failed'; message: string | null }

/**
 * Accept (optionally with the per-hunk merge) or reject a staged suggestion
 * and map the answer to what the strip should do. Never throws.
 */
export async function decideStagedSuggestion(
  sessionId: string,
  proposalId: string,
  decision: 'accepted' | 'rejected',
  mergedHtml?: string,
): Promise<StagedDecisionOutcome> {
  try {
    const result = await resolveStagedSuggestion(sessionId, proposalId, decision, mergedHtml)
    if (decision === 'rejected') return { kind: 'rejected' }
    return { kind: 'accepted', segment: result?.segment ?? null }
  } catch (error) {
    const code = error instanceof ApiRequestError ? error.code : (error as { code?: unknown } | null)?.code
    const status = error instanceof ApiRequestError ? error.statusCode : undefined
    const details = (error as { details?: Record<string, unknown> } | null)?.details
    if (code === 'SUGGESTION_OUTDATED') {
      return {
        kind: 'outdated',
        currentVersion: typeof details?.['currentVersion'] === 'number' ? details['currentVersion'] : null,
        currentContent: typeof details?.['currentContent'] === 'string' ? details['currentContent'] : null,
      }
    }
    if (code === 'PROPOSAL_ALREADY_RESOLVED') return { kind: 'already_accepted' }
    if (status === 404 || code === 'NOT_FOUND') return { kind: 'gone' }
    const message = status === 403 && error instanceof Error && error.message ? error.message : null
    return { kind: 'failed', message }
  }
}
